import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import { useAppDispatch, useAppSelector } from '../../store/hooks';
import { fetchRecipe, clearCurrentRecipe, saveRecipe, unsaveRecipe, shareRecipe, removeShare, deleteRecipe } from '../../store/recipesSlice';
import { fetchPricesForFoods, formatPrice } from '../../store/pricesSlice';
import useCsrfToken from '../../hooks/useCsrfToken';

const mdComponents = {
  p: ({ children }: React.PropsWithChildren) => <p className="mb-4 text-gray-300 leading-relaxed">{children}</p>,
  ol: ({ children }: React.PropsWithChildren) => <ol className="list-decimal list-outside pl-5 space-y-2 mb-4 text-gray-300">{children}</ol>,
  ul: ({ children }: React.PropsWithChildren) => <ul className="list-disc list-outside pl-5 space-y-2 mb-4 text-gray-300">{children}</ul>,
  li: ({ children }: React.PropsWithChildren) => <li className="leading-relaxed">{children}</li>,
  strong: ({ children }: React.PropsWithChildren) => <strong className="text-white font-semibold">{children}</strong>,
  em: ({ children }: React.PropsWithChildren) => <em className="text-gray-400 italic">{children}</em>,
  h2: ({ children }: React.PropsWithChildren) => <h2 className="text-white font-semibold text-lg mt-6 mb-2 first:mt-0">{children}</h2>,
  h3: ({ children }: React.PropsWithChildren) => <h3 className="text-white font-medium mt-4 mb-1">{children}</h3>,
};

const RecipeShow: React.FC = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const csrfToken = useCsrfToken();
  const { id } = useParams<{ id: string }>();
  const { currentRecipe: recipe, currentStatus: status, error } = useAppSelector((state) => state.recipes);
  const user = useAppSelector((state) => state.auth.user);
  const pricesByFoodId = useAppSelector((state) => state.prices.byFoodId);

  const [shareEmail, setShareEmail] = useState('');
  const [shareError, setShareError] = useState<string | null>(null);
  const [shareLoading, setShareLoading] = useState(false);

  useEffect(() => {
    if (id) dispatch(fetchRecipe(Number(id)));
    return () => { dispatch(clearCurrentRecipe()); };
  }, [id, dispatch]);

  useEffect(() => {
    if (!recipe) return;
    const foodIds = recipe.ingredients.map((i) => i.food_id).filter((id): id is number => id != null);
    if (foodIds.length > 0) dispatch(fetchPricesForFoods(foodIds));
  }, [recipe, dispatch]);

  if (status === 'loading' || status === 'idle') {
    return <div className="text-gray-500 py-12 text-center">Loading...</div>;
  }
  if (status === 'failed') {
    return <div className="text-rose-400 py-12 text-center">{error || 'Recipe not found or access denied.'}</div>;
  }
  if (!recipe) return null;

  const isOwner = recipe.can_edit;
  const isPublic = recipe.visibility === 'public';
  const canSave = isPublic && !!user && !isOwner;

  const handleDelete = () => {
    if (!confirm(`Delete "${recipe.name}"?`)) return;
    dispatch(deleteRecipe({ id: recipe.id, csrfToken }))
      .unwrap()
      .then(() => navigate('/recipes/mine'))
      .catch((err: Error) => alert('Failed to delete: ' + err.message));
  };

  const handleShare = async () => {
    if (!shareEmail.trim()) return;
    setShareLoading(true);
    setShareError(null);
    dispatch(shareRecipe({ recipeId: recipe.id, email: shareEmail.trim() }))
      .unwrap()
      .then(() => setShareEmail(''))
      .catch((err: Error) => setShareError(err.message))
      .finally(() => setShareLoading(false));
  };

  return (
    <div>
      {/* Header */}
      <div className="flex items-start justify-between mb-2">
        <div className="flex-1 min-w-0">
          <Link to="/recipes" className="text-xs text-gray-500 hover:text-white transition-colors mb-3 inline-block">
            ← Recipes
          </Link>
          <div className="flex items-center gap-3 mb-1">
            <h1 className="text-3xl font-bold text-white">{recipe.name}</h1>
            <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium shrink-0 ${
              isPublic
                ? 'bg-army-800 text-neon border border-army-700'
                : 'bg-navy-800 text-gray-400 border border-navy-700'
            }`}>
              {isPublic ? 'Public' : 'Private'}
            </span>
          </div>
          {recipe.description && (
            <p className="text-gray-400 mt-2 text-base leading-relaxed max-w-2xl">{recipe.description}</p>
          )}
          {!isOwner && recipe.visibility === 'private' && (
            <p className="text-xs text-gray-600 mt-2">Shared with you</p>
          )}
        </div>

        <div className="flex items-center gap-2 ml-6 shrink-0">
          {canSave && (
            <button
              onClick={() => recipe.is_saved ? dispatch(unsaveRecipe(recipe.id)) : dispatch(saveRecipe(recipe.id))}
              className={`px-4 py-2 text-sm font-medium rounded-lg border transition-colors ${
                recipe.is_saved
                  ? 'bg-neon/10 text-neon border-neon/30 hover:bg-rose-950 hover:text-rose-400 hover:border-rose-800'
                  : 'bg-navy-800 hover:bg-navy-700 text-gray-300 hover:text-white border-navy-700'
              }`}
            >
              {recipe.is_saved ? 'Saved' : 'Save'}
            </button>
          )}
          {isOwner && (
            <>
              <Link
                to={`/recipes/${recipe.id}/edit`}
                className="px-4 py-2 bg-navy-800 hover:bg-navy-700 text-gray-300 hover:text-white border border-navy-700 rounded-lg text-sm font-medium transition-colors"
              >
                Edit
              </Link>
              <button
                onClick={handleDelete}
                className="px-4 py-2 bg-navy-800 hover:bg-rose-950 text-gray-300 hover:text-rose-400 border border-navy-700 hover:border-rose-800 rounded-lg text-sm font-medium transition-colors"
              >
                Delete
              </button>
            </>
          )}
        </div>
      </div>

      {/* Body */}
      <div className="flex gap-6 mt-8 items-start">
        {/* Ingredients sidebar */}
        <div className="w-64 shrink-0 bg-navy-900 border border-navy-800 rounded-xl overflow-hidden sticky top-24">
          <div className="px-5 py-3 border-b border-navy-800">
            <h2 className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Ingredients</h2>
          </div>
          {recipe.ingredients.length === 0 ? (
            <p className="px-5 py-4 text-sm text-gray-500">No ingredients listed.</p>
          ) : (
            <ul className="divide-y divide-army-900">
              {recipe.ingredients.map((ing) => {
                const price = ing.food_id != null ? pricesByFoodId[ing.food_id] : null;
                return (
                  <li key={ing.id} className="px-5 py-3">
                    <span className="font-medium text-white text-sm">{ing.food_name}</span>
                    {ing.measurement && (
                      <span className="text-gray-400 text-sm"> — {ing.measurement}</span>
                    )}
                    {price && (
                      <div className="mt-0.5 text-xs">
                        {price.on_sale && price.sale_price_cents != null ? (
                          <>
                            <span className="text-neon font-medium">{formatPrice(price.sale_price_cents)}</span>
                            <span className="text-gray-600 line-through ml-1">{formatPrice(price.price_cents)}</span>
                            <span className="text-gray-600 ml-1">· {price.store_name}</span>
                          </>
                        ) : (
                          <>
                            <span className="text-gray-400">{formatPrice(price.price_cents)}</span>
                            <span className="text-gray-600 ml-1">· {price.store_name}</span>
                          </>
                        )}
                      </div>
                    )}
                  </li>
                );
              })}
            </ul>
          )}
        </div>

        {/* Instructions */}
        <div className="flex-1 min-w-0">
          <div className="bg-navy-900 border border-navy-800 rounded-xl px-8 py-6">
            <h2 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-5">Instructions</h2>
            {recipe.instructions?.trim() ? (
              <ReactMarkdown components={mdComponents}>{recipe.instructions}</ReactMarkdown>
            ) : (
              <p className="text-gray-500 text-sm">No instructions added yet.</p>
            )}
          </div>

          {/* Sharing section — owner only, private recipes */}
          {isOwner && !isPublic && (
            <div className="mt-6 bg-navy-900 border border-navy-800 rounded-xl px-6 py-5">
              <h2 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-4">Sharing</h2>

              {recipe.shares && recipe.shares.length > 0 && (
                <ul className="mb-4 space-y-2">
                  {recipe.shares.map((share) => (
                    <li key={share.id} className="flex items-center justify-between">
                      <span className="text-sm text-gray-300">{share.email}</span>
                      <button
                        onClick={() => dispatch(removeShare({ recipeId: recipe.id, shareId: share.id }))}
                        className="text-xs text-gray-600 hover:text-rose-400 transition-colors"
                      >
                        Remove
                      </button>
                    </li>
                  ))}
                </ul>
              )}

              {(!recipe.shares || recipe.shares.length === 0) && (
                <p className="text-sm text-gray-600 mb-4">Not shared with anyone yet.</p>
              )}

              <div className="flex gap-2">
                <input
                  type="email"
                  value={shareEmail}
                  onChange={(e) => { setShareEmail(e.target.value); setShareError(null); }}
                  onKeyDown={(e) => e.key === 'Enter' && handleShare()}
                  placeholder="Email address..."
                  className="flex-1 px-3 py-2 bg-army-950 border border-army-800 rounded-lg text-white placeholder-gray-600 focus:outline-none focus:border-neon text-sm transition-colors"
                />
                <button
                  onClick={handleShare}
                  disabled={shareLoading || !shareEmail.trim()}
                  className="px-4 py-2 bg-army-800 hover:bg-army-700 text-white border border-army-700 rounded-lg text-sm font-medium transition-colors disabled:opacity-40"
                >
                  {shareLoading ? 'Sharing…' : 'Share'}
                </button>
              </div>
              {shareError && <p className="mt-2 text-sm text-rose-400">{shareError}</p>}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default RecipeShow;
